import { apiEnums, useApiClient, type ApiModel } from '@nodal/api';
import { useConfirmationDialog } from '@nodal/uikit/components/ConfirmationDialog';
import { FileDropZone } from '@nodal/uikit/components/FileDropZone';
import { Modal } from '@nodal/uikit/components/Modal';
import { UploadedFileList } from '@nodal/uikit/components/UploadedFileList';
import { useState } from 'react';
import { useQuery } from 'react-query';

import { queryKeys } from 'consts/query';
import {
  transformDocumentsIntoUploadedFiles,
  useUploadInsuranceDocuments,
} from 'hooks/useUploadInsuranceDocuments';
import { useUser } from 'hooks/useUser';

import { getDetailsForSection, getSectionQuestionsByRole } from '../utils';

import type {
  ParentProfileInformation,
  ProfileData,
  QuestionData,
} from '../MatchProfile.interface';
import type { SectionDetailsProps } from './SectionDetails.interface';

export const InsuranceReviewSectionDetails = ({
  profile,
  role,
}: SectionDetailsProps<ProfileData>) => {
  const questions: QuestionData<ParentProfileInformation>[] =
    getSectionQuestionsByRole('insuranceReview', role);
  const filteredQuestions = questions.filter((q) => q.type !== 'image');
  const imageQuestions = questions.filter(
    (q: QuestionData<ParentProfileInformation>) => q.type === 'image',
  );
  const apiClient = useApiClient();
  const { user } = useUser();
  const confirm = useConfirmationDialog();
  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);

  // Get medical_review.documents if available (admin view)
  const profileWithMedicalReview = profile as {
    medical_review?: { documents?: ApiModel.MedicalDocument[] };
  } & typeof profile;
  const medicalReviewDocuments =
    profileWithMedicalReview?.medical_review?.documents || [];

  // Filter insurance documents
  const insuranceDocsFromMedicalReview = medicalReviewDocuments.filter(
    (doc) =>
      doc.document_type ===
      apiEnums.MedicalDocumentDocumentTypeEnum.SurroInsDoc,
  );

  // Filter income documents
  const incomeDocsFromMedicalReview = medicalReviewDocuments.filter(
    (doc) =>
      doc.document_type ===
      apiEnums.MedicalDocumentDocumentTypeEnum.SurroIncomeDoc,
  );

  const { data: insuranceDocuments } = useQuery(
    queryKeys.insuranceDocumentsList,
    () =>
      apiClient.api.MedicalReviewsApi.medicalReviewsInsuranceDocumentsOwnList(),
    {
      enabled: medicalReviewDocuments.length === 0, // Only fetch if no medical_review
    },
  );

  const { data: incomeDocuments } = useQuery(
    queryKeys.incomeDocumentsList,
    () =>
      apiClient.api.MedicalReviewsApi.medicalReviewsIncomeDocumentsOwnList(),
    {
      enabled: medicalReviewDocuments.length === 0, // Only fetch if no medical_review
    },
  );

  const formattedDocuments = transformDocumentsIntoUploadedFiles(
    insuranceDocsFromMedicalReview.length > 0
      ? insuranceDocsFromMedicalReview
      : insuranceDocuments?.data || [],
  );

  const formattedIncomeDocuments = transformDocumentsIntoUploadedFiles(
    incomeDocsFromMedicalReview.length > 0
      ? incomeDocsFromMedicalReview
      : incomeDocuments?.data || [],
  );

  const {
    onDelete: onDeleteInsuranceDoc,
    onUpload: onUploadInsuranceDoc,
    uploadedFiles: uploadedInsuranceFiles,
    disabled: insuranceUploadDisabled,
  } = useUploadInsuranceDocuments({
    documents:
      insuranceDocsFromMedicalReview.length > 0
        ? insuranceDocsFromMedicalReview
        : insuranceDocuments?.data || [],
    confirm,
    documentType: apiEnums.MedicalDocumentDocumentTypeEnum.SurroInsDoc,
    medicalReviewId: user?.medical_review_id,
    queryKey: queryKeys.insuranceDocumentsList,
  });

  const {
    onDelete: onDeleteIncomeDoc,
    onUpload: onUploadIncomeDoc,
    uploadedFiles: uploadedIncomeFiles,
    disabled: incomeUploadDisabled,
  } = useUploadInsuranceDocuments({
    documents:
      incomeDocsFromMedicalReview.length > 0
        ? incomeDocsFromMedicalReview
        : incomeDocuments?.data || [],
    confirm,
    documentType: apiEnums.MedicalDocumentDocumentTypeEnum.SurroIncomeDoc,
    medicalReviewId: user?.medical_review_id,
    queryKey: queryKeys.incomeDocumentsList,
  });

  const profileDataWithInsuranceReview = {
    ...profile,
    ...profile.insurance_review,
  };

  const renderValue = (type: string | undefined, value: string | undefined) => {
    switch (type) {
      case 'image':
        return (
          <img
            src={value}
            alt="Insurance document"
            className="max-w-xs rounded-lg"
          />
        );
      case 'checkbox':
        return value === 'no_insurance' || value === null ? 'Yes' : 'No';
      default:
        return value;
    }
  };

  return (
    <div className="flex flex-col w-full">
      {getDetailsForSection(
        profileDataWithInsuranceReview,
        filteredQuestions,
      ).map(({ label, value, type }) => {
        if (!value) return null;

        return (
          <div
            className="flex flex-col gap-2 justify-start items-start py-4 mb-4 border-y border-grey-forest-100 sm:flex-row sm:gap-0 sm:items-center sm:py-3"
            key={label}
          >
            <div className="space-y-2 w-fit">
              <h3 className="text-lg text-grey-forest-900">{label}</h3>
              <div className="text-grey-forest-500">
                {renderValue(type, value)}
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex flex-row flex-wrap gap-4">
        {getDetailsForSection(
          profileDataWithInsuranceReview,
          imageQuestions,
        ).map(({ label, value, type }, index, array) => {
          if (!value) return null;

          return (
            <div
              key={label}
              className={`pr-2 ${
                index !== array.length - 1
                  ? 'border-r-2 border-grey-forest-100'
                  : ''
              }`}
            >
              <div className="space-y-2 w-fit">
                <h3 className="text-lg text-grey-forest-900">{label}</h3>
                <div className="flex flex-row gap-2 text-grey-forest-500">
                  {renderValue(type, value)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col pt-4 w-full">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg text-grey-forest-900">Insurance Documents</h3>
          <button
            onClick={() => setIsInsuranceModalOpen(true)}
            className="text-sm font-medium text-forest-500 hover:text-forest-600 hover:underline"
            type="button"
          >
            Manage Documents
          </button>
        </div>
        {formattedDocuments.length > 0 ? (
          <UploadedFileList
            files={formattedDocuments}
            uploaderIconVisible={false}
            hideDeleteIcon
            hideProgress
            onDelete={() => Promise.resolve()}
          />
        ) : (
          <p className="text-grey-forest-500">
            No insurance documents uploaded
          </p>
        )}
      </div>
      <div className="flex flex-col pt-4 w-full">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg text-grey-forest-900">
            Income Related Documents
          </h3>
          <button
            onClick={() => setIsIncomeModalOpen(true)}
            className="text-sm font-medium text-forest-500 hover:text-forest-600 hover:underline"
            type="button"
          >
            Manage Documents
          </button>
        </div>
        {formattedIncomeDocuments.length > 0 ? (
          <UploadedFileList
            files={formattedIncomeDocuments}
            uploaderIconVisible={false}
            hideDeleteIcon
            hideProgress
            onDelete={() => Promise.resolve()}
          />
        ) : (
          <p className="text-grey-forest-500">No income documents uploaded</p>
        )}
      </div>

      <Modal
        open={isInsuranceModalOpen}
        onClose={() => setIsInsuranceModalOpen(false)}
        title="Insurance Documents"
        size="lg"
        disableClose={true}
      >
        <div className="p-8 space-y-6">
          <p className="text-base font-normal leading-6 text-grey-forest-600">
            Please upload any additional materials you have related to your
            health insurance coverage. Specifically, we're looking for your
            Explanation of Benefits packet. This booklet is typically 50+ pages
            in length and can be requested by your HR department or accessed
            through your Health Insurance Portal.
          </p>
          <div className="flex flex-col rounded-lg border border-grey-forest-100">
            <div className="border-b border-grey-forest-100">
              <FileDropZone
                onUpload={onUploadInsuranceDoc}
                disabled={insuranceUploadDisabled}
                acceptFileTypes="image/*, .pdf, .doc, .docx"
              />
            </div>
            <UploadedFileList
              files={uploadedInsuranceFiles}
              onDelete={onDeleteInsuranceDoc}
              uploaderIconVisible={false}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={isIncomeModalOpen}
        onClose={() => setIsIncomeModalOpen(false)}
        title="Income Related Documents"
        size="lg"
        disableClose={true}
      >
        <div className="p-8 space-y-6">
          <p className="text-base font-normal leading-6 text-grey-forest-600">
            Please upload your most recent paystub. We will use this information
            to estimate reimbursements for any lost wages during your journey.
          </p>
          <div className="flex flex-col rounded-lg border border-grey-forest-100">
            <div className="border-b border-grey-forest-100">
              <FileDropZone
                onUpload={onUploadIncomeDoc}
                disabled={incomeUploadDisabled}
                acceptFileTypes="image/*, .pdf, .doc, .docx"
              />
            </div>
            <UploadedFileList
              files={uploadedIncomeFiles}
              onDelete={onDeleteIncomeDoc}
              uploaderIconVisible={false}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
